import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  CacheKeys,
  checkIfUnitOfMeasureIsHours,
  ErrorBoundary,
  getUserUnitLabel,
  SearchField,
  SuspenseLoader,
  useAppUser,
  useDate,
  useDebounce,
  useName,
  useSessionStorage,
} from '@bechtel/client-common';
import { GET_PENDING_APPROVAL_LIST_SUMMARY_STATS, GET_WORKLIST_DATA } from '@bechtel/client-gql';
import {
  belowBreakpoint,
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Heading,
  Icon,
  IconSize,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldSize,
  Theme,
  useMediaQuery,
  useModalState,
  useSideSheetState,
} from '@bechtel/gds';
import {
  CommodityMilestone,
  CommodityType,
  MaterialCommodity,
  MilestoneStatusCode,
  MilestoneType,
  OwnershipLevel,
  PagingRowNumberInput,
  PendingApprovalInput,
  PendingApprovalSummary,
  SortOrder,
  WeeklyWorkInput,
  WeeklyWorkList,
  WeeklyWorkListBulkItem,
  WeeklyWorkListSearchBy,
  WeeklyWorkListSortByTerm,
  Weld,
} from '@bechtel/types';
import { formatISO, parseISO } from 'date-fns';
import { filter, find, get, isEqual, map } from 'lodash';
import { FC, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from 'styled-components';
//@ts-ignore declration not recognizing svg
import { ReactComponent as SearchResults } from '@bechtel/client-assets/dist/images/search-results.svg';
import { CommodityDetail } from '@bechtel/client-work-packages';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { AddViewAttachment } from '~/add-view-attachment';
import { useBulkWeeklyWorklist } from '~/hooks/use-bulk-weekly-worklist';
import { WeekStartType } from '~/types';
import { isWorkPackagePersona } from '~/utilities';
import { WorkListMenuType } from '../../worklist-detail-button/types';
import { DownloadWorkList } from '../../worklist-download';
import { WorkListDrawer as PendingApprovalListDrawer } from '../../worklist-drawer';
import { searchByList } from '../../worklist-drawer/drawer-state';
import { PendingApprovalListItem } from './item';
import {
  PendingApprovalListBodyStyled,
  PendingApprovalListGroupStyled,
  PendingApprovalListHeaderStyled,
  PendingApprovalListStyled,
  SearchCanvasButtonStyled,
  SearchFieldStyled,
  SearchIconStyled,
  TabButtonStyled,
  TabListStyled,
} from './styled';
import { PendingApprovalListProps, PendingApprovalSelectionStateProps, WorkListDrawerValueProps } from './types';

const initialDrawerValues: WorkListDrawerValueProps = {
  searchBy: WeeklyWorkListSearchBy.TAG_ID,
  filterByCommodity: [],
  filterByMilestoneTypes: [],
  filterByPlanStatusTypes: [],
  sortBy: WeeklyWorkListSortByTerm.TAG,
  sortDir: SortOrder.DESC,
};

export const PendingApprovalList: FC<PendingApprovalListProps> = ({ crewMember }) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();
  const { user } = useAppUser();
  const { getWeekNo } = useDate();
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const weekStartDate = currentProjectWeek?.startDate;
  const weekEndDate = currentProjectWeek?.endDate;
  const [downloadDataReady, setDownloadDataReady] = useState<boolean>(false);
  const [downloadStarted, setDownloadStarted] = useState<boolean>(false);
  const [updatedMilestoneId, setUpdatedMilestoneId] = useState<string>();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const [selectionStateValues, setSelectionStateValues] = useSessionStorage<PendingApprovalSelectionStateProps>(
    CacheKeys.PendingApprovalListSelectedState,
    { milestoneStatus: MilestoneStatusCode.CLAIMED }
  );
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const { close, toggle, visible } = useModalState(false);
  const persistedDrawerValues = useRef(initialDrawerValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<WorkListDrawerValueProps>(
    CacheKeys.PendingApprovalListDrawerValues,
    persistedDrawerValues.current || initialDrawerValues
  );
  const [selectedMilestone, setSelectedMilestone] = useState<CommodityMilestone>();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState<MaterialCommodity | Weld>();
  const debouncedSearchQuery = useDebounce(searchQuery?.trim(), 500);
  const pendingFilter = selectionStateValues?.milestoneStatus === MilestoneStatusCode.CLAIMED ? true : false;
  const weekStartsOn: WeekStartType = (
    user?.project?.projectConfiguration.weekStartDay ? user?.project?.projectConfiguration.weekStartDay : 1
  ) as WeekStartType; // Default start day is monday
  const currentWeekStartDay = weekStartDate;
  const selectedWeek = getWeekNo(parseISO(currentWeekStartDay), weekStartsOn);
  const pageLimit = 20;
  const bulkPageLimit = 10;

  const [loadPendingApprovalList, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    {
      weeklyWorkList: WeeklyWorkList;
    },
    {
      input: WeeklyWorkInput;
      paging?: PagingRowNumberInput;
    }
  >(GET_WORKLIST_DATA, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const [loadBulkWorklistData, { loading: bulkLoading, data: bulkData, fetchMore: bulkFetchMore }] =
    useBulkWeeklyWorklist(true);

  const [
    loadPendingApprovalListSummary,
    { loading: loadingSummary, data: summaryData, error: summaryError, refetch: summaryRefetch },
  ] = useLazyQuery<
    {
      weeklyWorkList: PendingApprovalSummary;
    },
    {
      input: PendingApprovalInput;
    }
  >(GET_PENDING_APPROVAL_LIST_SUMMARY_STATS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const {
    visible: visibleCommodityDetail,
    open: openCommodityDetailSideSheet,
    close: closeCommodityDetailSideSheet,
  } = useSideSheetState(false);

  const {
    visible: visibleMilestoneAttachment,
    open: openMilestoneAttachment,
    close: closeMilestoneAttachment,
  } = useSideSheetState(false);

  const pendingApprovalListItems = get(data, 'weeklyWorkList.items', []);
  const pendingApprovalSummary: PendingApprovalSummary = get(summaryData, 'pendingApprovalSummary');
  const currentRowNumber = get(data, 'weeklyWorkList.rowNumber', 0);
  const totalRecords = get(data, 'weeklyWorkList.total', 0);
  const bulkPALItems: WeeklyWorkListBulkItem[] = get(bulkData, 'weeklyWorkListBulk.items', []);
  let currentBulkRowNumber = get(bulkData, 'weeklyWorkListBulk.rowNumber', 0);
  const totalBulkRecords = get(bulkData, 'weeklyWorkListBulk.total', 0);

  const filteredPendingApprovalListItems = filter(pendingApprovalListItems, (item: CommodityMilestone) => {
    if (pendingFilter) {
      return (
        item.currentStatus?.codeI18n === MilestoneStatusCode.CLAIMED ||
        item.currentStatus?.codeI18n === MilestoneStatusCode.UNAPPROVED
      );
    }
    return item.currentStatus?.codeI18n === MilestoneStatusCode.APPROVED;
  });

  const searchLabel = find(searchByList, (searchByItem) => {
    return searchByItem.searchByTerm.type === drawerValues.searchBy;
  })?.label;

  const pendingApprovalInput: WeeklyWorkInput = {
    crewMemberId: crewMember?.id,
    personaType: crewMember?.persona,
    startDate: !pendingFilter ? formatISO(parseISO(currentWeekStartDay), { representation: 'date' }) : '',
    endDate: !pendingFilter ? formatISO(parseISO(weekEndDate), { representation: 'date' }) : '',
    searchValue: debouncedSearchQuery,
    ownershipLevel: OwnershipLevel.ALL,
    workPackageIds: [],
    searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
    filterByMilestoneTypes: map(
      drawerValues?.filterByMilestoneTypes,
      (milestoneType: MilestoneType) => milestoneType.id
    ),
    filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
    filterByMilestoneStatus: pendingFilter ? MilestoneStatusCode.CLAIMED : MilestoneStatusCode.APPROVED,
    filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
    sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WeeklyWorkListSortByTerm.TAG,
    sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortOrder.DESC,
  };

  useEffect(() => {
    if (
      (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) ||
      !crewMember ||
      !crewMember.id ||
      !crewMember.persona ||
      !isWorkPackagePersona(crewMember?.persona)
    )
      return;

    const pendingApprovalSummaryInput: PendingApprovalInput = {
      crewMemberId: crewMember?.id,
      personaType: crewMember?.persona,
      searchValue: debouncedSearchQuery,
      ownershipLevel: OwnershipLevel.ALL,
      workPackageIds: [],
      searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
      filterByMilestoneTypes: map(
        drawerValues?.filterByMilestoneTypes,
        (milestoneType: MilestoneType) => milestoneType.id
      ),
      filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
      filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
    };

    loadPendingApprovalList({
      variables: {
        input: pendingApprovalInput,
        paging: {
          rowNumber: 0,
          limit: pageLimit,
        },
      },
    });
    loadPendingApprovalListSummary({
      variables: {
        input: pendingApprovalSummaryInput,
      },
    });
  }, [
    loadPendingApprovalList,
    loadPendingApprovalListSummary,
    drawerValues,
    debouncedSearchQuery,
    user,
    weekStartsOn,
    currentWeekStartDay,
    pendingFilter,
    crewMember,
  ]);

  const onDrawerSubmit = useCallback(
    (values: WorkListDrawerValueProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  const fetchResult = async (actioned: boolean = false, limit?: number) => {
    const { data } = await fetchMore({
      variables: {
        paging: {
          rowNumber:
            actioned && filteredPendingApprovalListItems
              ? filteredPendingApprovalListItems.length - 1
              : currentRowNumber,
          limit: limit ? limit : pageLimit,
        },
      },
    });
    return get(data, 'weeklyWorkList.rowNumber', 0);
  };

  const handlScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      currentRowNumber < totalRecords &&
      !loading
    ) {
      fetchResult();
    }
  };

  const onWorkListMenuSelect = (workListMenuType: WorkListMenuType, milestone: CommodityMilestone) => {
    // @ts-ignore ignore undefined tupe warning as commodity wrapper will either have a weld or material commodity always
    const commodity: MaterialCommodity | Weld = milestone.commodity.materialCommodity
      ? milestone.commodity.materialCommodity
      : milestone.commodity.weld;
    if (workListMenuType === WorkListMenuType.COMMODITY_DETAIL) {
      setSelectedCommodity(commodity);
      openCommodityDetailSideSheet();
    } else if (workListMenuType === WorkListMenuType.ADD_VIEW_PHOTOS) {
      setSelectedMilestone(milestone);
      openMilestoneAttachment();
    }
  };

  const handleActionCompleted = () => {
    if (
      filteredPendingApprovalListItems &&
      filteredPendingApprovalListItems.length <= pageLimit &&
      currentRowNumber < totalRecords
    )
      fetchResult(true);
  };

  const fetchBulkResult = async () => {
    const { data: bulkData } = await bulkFetchMore({
      variables: {
        paging: {
          rowNumber: currentBulkRowNumber,
          limit: bulkPageLimit,
        },
      },
    });
    return get(bulkData, 'weeklyWorkListBulk.rowNumber', 0);
  };

  const downloadWWL = async () => {
    currentBulkRowNumber = 0;
    if (downloadDataReady) {
      setDownloadDataReady(false);
    }
    setDownloadStarted(true);
    await loadBulkWorklistData({
      variables: {
        input: pendingApprovalInput,
        paging: {
          rowNumber: currentBulkRowNumber,
          limit: bulkPageLimit,
        },
      },
    });
  };

  const getAllBulkRecords = async () => {
    let hasNextPage = currentBulkRowNumber < totalBulkRecords;
    while (hasNextPage && !bulkLoading) {
      currentBulkRowNumber = await fetchBulkResult();
      hasNextPage = currentBulkRowNumber < totalBulkRecords;
    }

    if (!hasNextPage && totalBulkRecords) {
      setDownloadDataReady(true);
      setDownloadStarted(false);
    }
  };

  useEffect(() => {
    getAllBulkRecords();
  }, [bulkData, currentBulkRowNumber, downloadDataReady]);

  const isUnitOfMeasureHour = checkIfUnitOfMeasureIsHours(user);
  const unitLabel = getUserUnitLabel(user, true);

  return (
    <PendingApprovalListStyled>
      <PendingApprovalListHeaderStyled alignItems={{ sm: 'center' }}>
        <Heading style={{ flex: 1 }} size={isWideScreen ? '500' : '600'} weight="600">
          <Trans>Pending Approval List</Trans>
        </Heading>
        <SearchFieldStyled>
          <SearchField
            size={TextFieldSize.SMALL}
            iconSize={IconSize.SMALL}
            placeholder={t`Search ${i18n._(searchLabel)}`} //There will atleast one search by selected always
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
        </SearchFieldStyled>
        <Box mr={{ sm: '400' }}>
          <SearchCanvasButtonStyled
            size={ButtonSize.SMALL}
            appearance={ButtonAppearance.SECONDARY}
            isDefaultFilterChange={!isEqual(drawerValues, initialDrawerValues)}
            onClick={toggle}
          >
            <SearchIconStyled
              icon="sliders-v"
              size={IconSize.MEDIUM}
              fillColor={!isEqual(drawerValues, initialDrawerValues) ? 'white' : ''}
            />
          </SearchCanvasButtonStyled>
        </Box>
        <DownloadWorkList
          milestoneStatus={selectionStateValues?.milestoneStatus}
          downloadWWL={downloadWWL}
          userName={crewMember ? getFullName(crewMember.firstName, crewMember.lastName) : ''}
          loading={bulkLoading || downloadStarted}
          workListItems={bulkPALItems}
          downloadDataReady={downloadDataReady}
          setDownloadDataReady={setDownloadDataReady}
          selectedWeek={selectedWeek}
          disabled={filteredPendingApprovalListItems?.length === 0 || bulkLoading || downloadStarted}
        />
        <Box>
          <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.SECONDARY} disabled={true}>
            <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" />
          </Button>
        </Box>
      </PendingApprovalListHeaderStyled>
      {crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona) && (
        <TabListStyled>
          <TabButtonStyled
            selected={pendingFilter}
            onClick={() => {
              setSelectionStateValues({ milestoneStatus: MilestoneStatusCode.CLAIMED });
            }}
          >
            <Box flexDirection={{ sm: 'column' }}>
              <Text size="400" color="grey.1000">
                <Trans>Pending</Trans>
                {!loadingSummary &&
                  ` (${
                    isUnitOfMeasureHour
                      ? pendingApprovalSummary?.pendingApprovalHours + t`hrs`
                      : pendingApprovalSummary?.pendingApprovalQuantity?.quantity + unitLabel
                  })`}
              </Text>
            </Box>
          </TabButtonStyled>
          <TabButtonStyled
            selected={!pendingFilter}
            onClick={() => {
              setSelectionStateValues({ milestoneStatus: MilestoneStatusCode.APPROVED });
            }}
          >
            <Box flexDirection={{ sm: 'column' }}>
              <Text size="400" color="grey.1000">
                <Trans>Approved This Week</Trans>
                {!loadingSummary &&
                  ` (${
                    isUnitOfMeasureHour
                      ? pendingApprovalSummary?.approvedHours + t`hrs`
                      : pendingApprovalSummary?.approvedQuantity?.quantity + unitLabel
                  })`}
              </Text>
            </Box>
          </TabButtonStyled>
        </TabListStyled>
      )}
      <PendingApprovalListBodyStyled>
        {crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona) ? (
          <>
            {filteredPendingApprovalListItems?.length > 0 && (
              <PendingApprovalListGroupStyled
                onScroll={(e) => {
                  handlScroll(e);
                }}
              >
                {map(filteredPendingApprovalListItems, (item) => {
                  return (
                    <ErrorBoundary>
                      <PendingApprovalListItem
                        isPendingTabSelected={pendingFilter}
                        key={item.id}
                        item={item}
                        isOutOfPlan={
                          pendingFilter &&
                          (!item.plannedStartDate ||
                            selectedWeek !== getWeekNo(parseISO(item.plannedStartDate), weekStartsOn))
                        }
                        onWorkListMenuSelect={(workListMenuType) => {
                          onWorkListMenuSelect(workListMenuType, item);
                        }}
                        crewMember={crewMember}
                        weekStartDate={weekStartDate}
                        weekEndDate={weekEndDate}
                        onActionUpdating={() => {
                          setUpdatedMilestoneId(item.id);
                        }}
                        onActionCompleted={() => {
                          handleActionCompleted();
                          setUpdatedMilestoneId('');
                        }}
                        disableAction={updatedMilestoneId && updatedMilestoneId !== item.id ? true : false}
                      />
                    </ErrorBoundary>
                  );
                })}
              </PendingApprovalListGroupStyled>
            )}
            {!(loading || loadingSummary) && (error || summaryError) && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                p={{ sm: '500' }}
              >
                <Box pb={{ sm: '500' }}>
                  <Text>
                    <Trans>
                      There was a problem loading pending approval list items. Please click the button below to try
                      again.
                    </Trans>
                  </Text>
                </Box>
                <Box>
                  <Button
                    onClick={() => {
                      refetch();
                      summaryRefetch();
                    }}
                  >
                    <Box mr={{ sm: '400' }}>
                      <Icon icon="undo" />
                    </Box>
                    <Trans>Retry</Trans>
                  </Button>
                </Box>
              </Box>
            )}
            {!loading && !error && filteredPendingApprovalListItems?.length === 0 && (
              <Box
                flexDirection={{ sm: 'column' }}
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                p={{ sm: '500' }}
              >
                <Text size="400" family="graphik" color="grey.600">
                  {pendingFilter ? (
                    <Trans>No pending approval items available!!!</Trans>
                  ) : (
                    <Trans>No approved items available!!!</Trans>
                  )}
                </Text>
              </Box>
            )}
            {(!called || networkStatus === NetworkStatus.refetch || loading) && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                p={{ sm: '500' }}
                data-testid="loading"
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
          </>
        ) : (
          <Box
            flexDirection={{ sm: 'column' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            data-testid="pending-approval-list-empty"
          >
            <SearchResults style={{ width: '124px', height: '110px' }} />
            <Heading size="400" weight="600" color="grey.600">
              <Trans>Nothing to show yet!</Trans>
            </Heading>
            <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
              <Text
                size="400"
                family="graphik"
                color="grey.600"
                //  line height is required because when sentence wraps in two lines gap between both lines are required.
                style={{ textAlign: 'center', lineHeight: '1.57' }}
              >
                <Trans>
                  Once you select a user, you will be able to see all pending and completed approvals in this week plan.
                </Trans>
              </Text>
            </Box>
          </Box>
        )}
      </PendingApprovalListBodyStyled>
      {/* <PendingApprovalFooterStyled>
        <Box justifyContent={{ sm: 'flex-end' }} flexGrow={{ sm: 1 }}>
          <Box>
            <DrawerFooterButtonStyled
              data-testid="approve-all-button"
              type="submit"
              size={ButtonSize.MEDIUM}
              appearance={ButtonAppearance.SECONDARY}
              disabled={true}
            >
              <Trans>Approve All</Trans>
            </DrawerFooterButtonStyled>
          </Box>
        </Box>
      </PendingApprovalFooterStyled> */}
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visible && (
            <PendingApprovalListDrawer close={close} initialValues={drawerValues} onSubmit={onDrawerSubmit} />
          )}
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleCommodityDetail && selectedCommodity && (
            <CommodityDetail
              commodityId={selectedCommodity?.id}
              commodityName={selectedCommodity?.type.name}
              commodityTag={selectedCommodity?.tag}
              workPackageTag={selectedCommodity?.workPackage?.tag}
              close={closeCommodityDetailSideSheet}
              showDocuments={true}
            />
          )}
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleMilestoneAttachment && selectedMilestone && (
            <AddViewAttachment milestone={selectedMilestone} closeModal={closeMilestoneAttachment} />
          )}
        </Suspense>
      </ErrorBoundary>
    </PendingApprovalListStyled>
  );
};
